
import { useParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useProfile } from "@/hooks/useProfile";
import ProfileHeader from "@/components/profile/ProfileHeader";
import ProfileForm from "@/components/profile/ProfileForm";
import ProfileView from "@/components/profile/ProfileView";
import PromptsSection from "@/components/profile/PromptsSection";
import LoadingState from "@/components/profile/LoadingState";
import NotFoundState from "@/components/profile/NotFoundState";
import LoadingDebugger from "@/components/debugger/LoadingDebugger";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

const UserProfilePage = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>("profile");
  
  const isOwnProfile = user && (id === user.id || !id);
  const userId = id || user?.id;
  
  const {
    profile,
    isLoading,
    isEditing,
    setIsEditing,
    prompts,
    form,
    handleUpdateProfile,
    refreshProfile,
    error
  } = useProfile(userId, !!isOwnProfile);
  
  useEffect(() => {
    if (!userId) {
      console.error("No user ID available for profile");
    }
  }, [userId]);
  
  console.log(`UserProfilePage: id=${userId}, isLoading=${isLoading}, hasProfile=${!!profile}, promptCount=${prompts.length}`);
  
  if (isLoading) {
    return (
      <>
        <Navbar />
        <LoadingState />
        <LoadingDebugger id="UserProfilePage" isLoading={isLoading} dataCount={0} error={error} />
        <Footer />
      </>
    );
  }
  
  if (!profile) {
    return (
      <>
        <Navbar />
        <NotFoundState />
        <LoadingDebugger id="UserProfilePage" isLoading={isLoading} dataCount={0} error={error} />
        <Footer />
      </>
    );
  }
  
  const fullName = 
    `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || 
    "User";
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          <div className="flex justify-between items-center mb-6">
            <h1 className="sr-only">User Profile: {fullName}</h1>
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={refreshProfile}
              className="flex items-center gap-1"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
          
          <ProfileHeader 
            profile={profile}
            isOwnProfile={!!isOwnProfile}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
          
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="prompts">Prompts ({prompts.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="profile">
              {isEditing ? (
                <ProfileForm 
                  form={form}
                  onSubmit={handleUpdateProfile}
                  onCancel={() => setIsEditing(false)}
                />
              ) : (
                <ProfileView 
                  profile={profile}
                  isOwnProfile={!!isOwnProfile}
                />
              )}
            </TabsContent>
            
            <TabsContent value="prompts">
              <PromptsSection 
                prompts={prompts}
                isOwnProfile={!!isOwnProfile}
                fullName={fullName}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      <Footer />
      <LoadingDebugger id="UserProfilePage" isLoading={isLoading} dataCount={prompts.length} error={error} />
    </div>
  );
};

export default UserProfilePage;
